import React from 'react';
import { Tooltip } from '@material-ui/core';
import { LinearProgressWithLabel } from 'components';


interface IRunProgress {
  run: IBulkPriceRun;
  progressByTarget: Record<string, IBulkPriceProgress>;
  liveCounters: IBulkPriceCounters | null;
}

const STATUS_CLASS: Record<string, string> = {
  PENDING: 'text-muted',
  RUNNING: 'text-primary',
  DONE: 'text-success',
  SKIPPED: 'text-muted',
  FAILED: 'text-error'
};

const RunProgress = ({ run, progressByTarget, liveCounters }: IRunProgress) => {
  // Live socket counters when they are ahead of the last poll, otherwise the polled run.
  const counters = liveCounters || run.counters;
  const total = Number(run.counters?.totalItems) || 0;
  const processed = Number(counters?.processedItems) || 0;
  const percent = total ? Math.min(100, (processed * 100) / total) : 0;

  return (
    <div className="mt-4 text-left">
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-16">
          {run.dryRun ? 'Preview' : 'Run'} {run.status.toLowerCase()}
        </span>
      </div>
      <LinearProgressWithLabel value={percent} />
      <div className="text-13 text-muted mb-3">
        {processed} of {total} rows processed &middot; {counters?.updated || 0} updated &middot;
        {' '}{counters?.skipped || 0} skipped &middot; {counters?.notFound || 0} not found &middot;
        {' '}{counters?.brandMismatch || 0} brand mismatch &middot; {counters?.error || 0} errors
      </div>

      {Boolean(run.error?.message) &&
        <div className="text-error text-13 mb-2">{run.error?.kind}: {run.error?.message}</div>
      }

      <div className="scroll-x">
        <table className="w-full text-13">
          <thead>
            <tr>
              <th className="text-left p-1">Store</th>
              <th className="text-left p-1">Store view</th>
              <th className="text-left p-1">Status</th>
              <th className="text-left p-1">Rows</th>
              <th className="text-left p-1">Updated</th>
              <th className="text-left p-1">Skipped</th>
              <th className="text-left p-1">Errors</th>
              <th className="text-left p-1">Reindexed</th>
              <th className="text-left p-1">Detail</th>
            </tr>
          </thead>
          <tbody>
            {run.targets.map((target, index) => {
              const live = progressByTarget[String(index)];
              const status = live?.status || target.status;
              const targetCounters = live?.counters || target.counters;
              const error = live?.error || target.error;
              // A batch is one Magento call that can take minutes, so say which one is in the
              // air. Without it the row reads as stalled between two percentage steps.
              const isBatchInFlight = Boolean(live?.chunkInFlight) && status === 'RUNNING';
              const isReindexing = Boolean(live?.reindexing) && status === 'RUNNING';

              return (
                <tr key={`${target.label}-${index}`}>
                  <td className="p-1">{target.label}</td>
                  <td className="p-1">{target.storeCode || '-'}</td>
                  <td className={`p-1 ${STATUS_CLASS[status] || ''}`}>
                    {status}
                    {isBatchInFlight &&
                      <div className="text-muted">
                        batch {Number(live?.chunkIndex) + 1} of {live?.chunksTotal} in progress
                      </div>
                    }
                    {isReindexing &&
                      <div className="text-muted">reindexing and cleaning cache</div>
                    }
                  </td>
                  <td className="p-1">{targetCounters?.processed || 0} / {target.counters?.total || 0}</td>
                  <td className="p-1">{targetCounters?.updated || 0}</td>
                  <td className="p-1">{targetCounters?.skipped || 0}</td>
                  <td className="p-1">{targetCounters?.error || 0}</td>
                  <td className="p-1">{target.reindexed ? 'yes' : '-'}</td>
                  <td className="p-1">
                    {error?.message
                      ? (
                        <Tooltip title={error.message} placement="top">
                          <span className="text-error">{error.kind}</span>
                        </Tooltip>
                      )
                      : target.reason || ''
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(RunProgress);
